import { Spinner } from "@/components/ui/spinner";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowRightIcon } from "lucide-react";
import { useAuth, useOrganizationList } from "@clerk/react";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import type { OnboardingData } from "@/pages/Onboarding";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

interface AffiliatePageLinkProps {
  formData: OnboardingData;
  updateFormData: (data: Partial<OnboardingData>) => void;
  handleNext: () => void;
}

const formSchema = z.object({
  affiliate_page_link: z.string().refine(
    (val) => {
      if (!val) return true; // optional field
      try {
        const url = new URL(val);
        return ["http:", "https:"].includes(url.protocol);
      } catch {
        return false;
      }
    },
    { message: "URL must be valid and start with http:// or https://" }
  ),
});

type FormData = z.infer<typeof formSchema>;

export default function AffiliatePageLink({
  formData,
  updateFormData,
  handleNext,
}: AffiliatePageLinkProps) {
  const { userId, getToken } = useAuth();
  const { setActive } = useOrganizationList({
    userMemberships: { infinite: true },
  });

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      affiliate_page_link: formData.affiliate_page_link || "",
    },
  });

  const onSubmit = async (data: FormData) => {
    try {
      const token = await getToken();
      const response = await fetch(
        `${API_BASE_URL}/api/auth/createOrganization`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            user_id: userId,
            ...formData,
            ...data,
          }),
        }
      );

      if (!response.ok) throw new Error("Failed to complete onboarding");

      const result = await response.json();
      if (result.success) {
        if (result.organization_id) {
          await setActive?.({ organization: result.organization_id });
        }
        toast.success("Created organization successfully!");
        handleNext();
      }
    } catch (error) {
      console.error("Error during onboarding:", error);
      toast.error("Failed to complete onboarding. Please try again.");
    }
  };

  return (
    <div className="w-full max-w-md">
      <div className="w-full">
        <div className="space-y-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="affiliate_page_link"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Affiliate Page Link{" "}
                      <span className="text-muted-foreground text-xs">
                        (optional)
                      </span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Link you show creators about your affiliate program"
                        className="max-w-md w-full"
                        onChange={(e) => {
                          field.onChange(e);
                          updateFormData({
                            affiliate_page_link: e.target.value,
                          });
                        }}
                        autoFocus
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                type="submit"
                className="w-md group"
                disabled={form.formState.isSubmitting}
              >
                Create Organization & Continue
                {!form.formState.isSubmitting && (
                  <ArrowRightIcon className="group-hover:translate-x-0.5 group-active:translate-x-1 transition-all" />
                )}
                {form.formState.isSubmitting && (
                  <Spinner />
                )}
              </Button>
            </form>
          </Form>
        </div>
      </div>
    </div>
  );
}
